import { Loader2, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  type Rider,
  useInviteRider,
  useRemoveRider,
  useRestoreRider,
  useRiders,
  useSuspendRider,
} from "@/hooks/use-riders";

type EligibilityLabelKey = "suspended" | "unavailable" | "available" | "unknown";

function eligibilityLabelKey(rider: Rider): EligibilityLabelKey {
  if (rider.status === "suspended") {
    return "suspended";
  }
  if (rider.status === "active") {
    return "available";
  }
  if (rider.status === "offline") {
    return "unavailable";
  }
  return "unknown";
}

function eligibilityLabel(t: (key: string, opts?: { defaultValue?: string }) => string) {
  return (key: EligibilityLabelKey) => {
    const labels: Record<EligibilityLabelKey, { key: string; fallback: string }> = {
      available: { fallback: "Disponible", key: "rider.eligibility.available" },
      suspended: { fallback: "Suspendido", key: "rider.eligibility.suspended" },
      unavailable: { fallback: "No disponible", key: "rider.eligibility.unavailable" },
      unknown: { fallback: "Desconocido", key: "rider.eligibility.unknown" },
    };
    return t(labels[key].key, { defaultValue: labels[key].fallback });
  };
}

export default function RidersPage() {
  const { t } = useTranslation();
  const ridersQuery = useRiders();
  const inviteMutation = useInviteRider();
  const removeMutation = useRemoveRider();
  const suspendMutation = useSuspendRider();
  const restoreMutation = useRestoreRider();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState("");

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await inviteMutation.mutateAsync(email.trim());
      toast.success(t("rider.list.inviteSuccess", "Invitación enviada"));
      setEmail("");
      setInviteOpen(false);
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const handleRemove = async (rider: Rider) => {
    const confirmed = window.confirm(
      t("rider.list.removeConfirm", `¿Quitar a ${rider.name} como repartidor?`),
    );
    if (!confirmed) {
      return;
    }
    try {
      await removeMutation.mutateAsync(rider.memberId);
      toast.success(t("rider.list.removeSuccess", "Repartidor removido"));
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const handleSuspend = async (rider: Rider) => {
    const confirmed = window.confirm(
      t(
        "rider.list.suspendConfirm",
        `¿Suspender a ${rider.name}? No podrá recibir nuevos pedidos hasta que lo restaures.`,
      ),
    );
    if (!confirmed) {
      return;
    }
    try {
      await suspendMutation.mutateAsync(rider.memberId);
      toast.success(t("rider.list.suspendSuccess", "Repartidor suspendido"));
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const handleRestore = async (rider: Rider) => {
    const confirmed = window.confirm(
      t(
        "rider.list.restoreConfirm",
        `¿Restaurar a ${rider.name}? Quedará no disponible hasta que se marque disponible desde la app.`,
      ),
    );
    if (!confirmed) {
      return;
    }
    try {
      await restoreMutation.mutateAsync(rider.memberId);
      toast.success(t("rider.list.restoreSuccess", "Repartidor restaurado"));
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold">{t("rider.list.title", "Repartidores")}</h1>
          <p className="text-muted-foreground text-sm" data-testid="rider.eligibility.help">
            {t("rider.list.eligibilityHelp", {
              defaultValue:
                "Administrás si un repartidor puede recibir pedidos (suspendido o disponible). La disponibilidad (en línea / no disponible) la maneja cada repartidor desde su app.",
            })}
          </p>
        </div>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button>{t("rider.list.invite", "Invitar repartidor")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("rider.list.inviteTitle", "Invitar repartidor")}</DialogTitle>
              <DialogDescription>
                {t(
                  "rider.list.inviteDescription",
                  "Le enviaremos un email para que se una como repartidor.",
                )}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleInvite} className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="email">{t("rider.list.email", "Email")}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>
                  {t("common.cancel", "Cancelar")}
                </Button>
                <Button type="submit" disabled={inviteMutation.isPending}>
                  {inviteMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t("rider.list.send", "Enviar invitación")
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {ridersQuery.isPending ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (ridersQuery.data?.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            {t("rider.list.empty", "No tenés repartidores. Invitá uno para empezar.")}
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-3">
          {ridersQuery.data?.map((rider) => {
            const eligibility = eligibilityLabelKey(rider);
            const suspended = eligibility === "suspended";
            const label = eligibilityLabel(t)(eligibility);
            return (
              <li key={rider.memberId}>
                <Card data-testid={`rider-row-${rider.memberId}`}>
                  <CardHeader className="flex-row items-center justify-between space-y-0">
                    <div>
                      <CardTitle className="text-base">{rider.name}</CardTitle>
                      <p className="text-xs text-muted-foreground">{rider.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        data-testid={`rider-state-${rider.memberId}`}
                        data-state={eligibility}
                        data-label={label}
                        variant={eligibility === "available" ? "secondary" : "outline"}
                      >
                        {label}
                      </Badge>
                      {suspended ? (
                        <Button
                          data-testid={`rider-restore-${rider.memberId}`}
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => handleRestore(rider)}
                          disabled={restoreMutation.isPending}
                        >
                          {t("rider.list.restore", "Restaurar")}
                        </Button>
                      ) : (
                        <Button
                          data-testid={`rider-suspend-${rider.memberId}`}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleSuspend(rider)}
                          disabled={suspendMutation.isPending}
                        >
                          {t("rider.list.suspend", "Suspender")}
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        onClick={() => handleRemove(rider)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground">
                    {rider.vehicleType ? (
                      <p>
                        {t("rider.list.vehicle", "Vehículo")}: {rider.vehicleType}
                      </p>
                    ) : null}
                    {rider.licensePlate ? (
                      <p>
                        {t("rider.list.plate", "Patente")}: {rider.licensePlate}
                      </p>
                    ) : null}
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
